import { useAppSelector } from '@store/hooks';
import { getUserList } from '@store/user/user.slice';
import { UserInfoItem, UserType, WeMeetClient } from '@tencent/wemeet-websdk-next';
import { hasScreen, isUserAudioOn, isUserVideoOn } from '@utils/helper/media';

/**
 * @description 获取用户的视频列表权重和成员列表权重，这里参考老逻辑实现
 * @param user
 * @returns
 */
const getUserVideoAndMemberOrder = (
  user: UserInfoItem,
): {
  memberOrder: string;
  videoOrder: string;
} => {
  // memberlistOrder是成员列表排序权重，两位的字符串，默认为 '00'
  // videolistOrder是右侧视频列表排序权重，三位的字符串，默认为 '000'
  // memberlistOrder首位，我：4；主持人：3；联席主持人：2；举手用户：1；普通成员：0
  let memberlistOrderFirst = '0';
  // memberlistOrder第二位，开启屏幕共享：4；开启麦克风：3；开启视频：2；开启录制：1； 都不开启：0
  let memberlistOrderSecond = '0';
  // videolistOrder首位，开启屏幕共享：2；开启视频：1；都不开启：0
  let videolistOrderFirst = '0';
  // videolistOrder第二位，自己：3；主持人：2；联席主持人：1；普通成员：0
  let videolistOrderSecond = '0';
  // videolistOrder第三位，没开视频：0；开启视频：cameraOrder(开启摄像头顺序)
  // 2021.3.11 上面那个不对 videolistOrder第三位，没开视频：0；开启视频：1
  let videolistOrderThird = '0';

  // FIXME user上没有hands_up的声明
  // if (user?.hands_up) {
  //   memberlistOrderFirst = '1'
  // }
  if (WeMeetClient.utils.isCoHost(user)) {
    memberlistOrderFirst = '2';
    videolistOrderSecond = '1';
  }
  if (WeMeetClient.utils.isHost(user)) {
    memberlistOrderFirst = '3';
    videolistOrderSecond = '2';
  }
  // FIXME 需要在user中声明 isSelf
  if (user.isSelf) {
    memberlistOrderFirst = '4';
    videolistOrderSecond = '3';
  }

  // 摄像头打开
  if (isUserVideoOn(user)) {
    memberlistOrderSecond = '2';
    videolistOrderThird = '1';
    videolistOrderFirst = '1';
  }

  // 开启音频
  if (isUserAudioOn(user)) {
    memberlistOrderSecond = '3';
  }

  // 屏幕分享
  if (hasScreen(user)) {
    memberlistOrderSecond = '4';
    videolistOrderFirst = '2';
  }

  return {
    videoOrder: `${videolistOrderFirst}${videolistOrderSecond}${videolistOrderThird}`,
    memberOrder: `${memberlistOrderFirst}${memberlistOrderSecond}`,
  };
};
/**
 *
 * 右侧小画面列表排序规则
 * 1。 先按照是否开视频：屏幕共享>开视频>没开视频
 * 2. 相同视频状态再按照身份：自己 > 主持人 > 联席主持人 >  其他成员
 * 3. 上述2者都相同则按照：开视频（开摄像头顺序 > 加会顺序），没开视频 （按首字母排序）
 */
export const userListSortHelper = (userList: UserInfoItem[]) =>
  [...userList]
    .filter((user) => user?.id) // 加入会议的时候存在没有用户id的情况, 或者是 userJoin的时候匹配不到用户，list里存在了一个只有rtcStream的对象
    .sort((a, b) => {
      const { videoOrder: videoOrderOfa } = getUserVideoAndMemberOrder(a);
      const { videoOrder: videoOrderOfb } = getUserVideoAndMemberOrder(b);

      if (videoOrderOfa === videoOrderOfb) {
        return (a?.participation_status?.['@last_update_time'] ?? 0) >
          (b?.participation_status?.['@last_update_time'] ?? 0)
          ? 1
          : -1;
      }
      return videoOrderOfa > videoOrderOfb ? -1 : 1;
    });
/**
 *
 * 最右侧参会人列表
 * 1. 先以角色划分：我>主持人>联席主持人>举手用户>成员
 * 2. 同角色内部划分设备状态：开启屏幕共享>开启麦克风>开启视频>都不开启
 * 3. 设备状态相同：按首字母排序
 */
const attendeeSortHelper = (userList: UserInfoItem[]) =>
  userList
    .filter((user) => user?.rtcStream?.streamType !== 'auxiliary' && user?.av_uid) // 过滤屏幕共享流且存在用户avuid，加入会议的时候存在没有用户avuid的情况(流比用户先来)
    .sort((a, b) => {
      const { memberOrder: memberOrderOfa } = getUserVideoAndMemberOrder(a);
      const { memberOrder: memberOrderOfb } = getUserVideoAndMemberOrder(b);
      if (memberOrderOfa === memberOrderOfb) {
        return (a?.nickname ?? '') > (b?.nickname ?? '') ? -1 : 1;
      }
      return memberOrderOfa > memberOrderOfb ? -1 : 1;
    });

/**
 * @description 获取排序后的右侧视频列表用户
 */
export const useUserList = () => {
  const userList = useAppSelector(getUserList);

  return userListSortHelper(userList);
};

export const useAttendee = () => {
  const userList = useAppSelector(getUserList);

  return attendeeSortHelper(userList);
};

export const useUserMap = () => {
  const userList = useAppSelector(getUserList);

  return userList.reduce((total: any, item: UserInfoItem) => {
    const { nickname } = item;
    const avUid = item?.av_uid ?? '';
    const instanceId = item?.id?.['@instance_id'];
    const appId = item?.id?.['@app_id'];
    const uid = item?.id?.['@app_uid'];
    const userType = item?.id?.['@user_type'];
    const userId = userType === UserType.PSTN_USER ? avUid : `${instanceId}_${uid}_${appId}`;

    if (userId.length > 2) {
      // eslint-disable-next-line no-param-reassign
      total[userId] = Object.assign(total[userId] || {}, { nickname });
    }
    return total;
  }, {});
};
